import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import './OfficeLayout.css';
import { officeAuthApi } from '../../services/tradeLicenseApi';

import OfficeLogin from './OfficeLogin';
import Dashboard from './Dashboard';
import ApplicationsList from './Applications/ApplicationsList';
import PendingApprovalsList from './Applications/PendingApprovalsList';
import ApplicationDetail from './Applications/ApplicationDetail';
import ApplicationCreate from './Applications/ApplicationCreate';
import InspectionsList from './Inspections/InspectionsList';
import PaymentsList from './Payments/PaymentsList';
import LicensesList from './Licenses/LicensesList';
import ReportsList from './Reports/ReportsList';
import Settings from './Settings/Settings';
import UserManagement from './Users/UserManagement';
import AuditLogs from './AuditLogs';
import CertificateView from '../TradeLicense/CertificateView';
import CounterPaymentCollection from './CounterPaymentCollection';

const OfficeLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [notifications] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // const [expandedSections, setExpandedSections] = useState({
  //   applications: false,
  //   operations: false,
  //   licenses: false,
  //   reports: false,
  //   administration: false,
  //   configuration: false,
  // });

  useEffect(() => {
    checkAuth();
  }, []);

  

  const checkAuth = async () => {
    setAuthLoading(true);
    try {
      await officeAuthApi.ensureCsrf();
      const response = await officeAuthApi.me();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    navigate('/office/dashboard');
  };

  const handleLogout = async () => {
    try {
      await officeAuthApi.logout();
    } catch (e) {
      // ignore
    }
    setUser(null);
    navigate('/office');
  };
const [popupSection, setPopupSection] = useState(null);

const openPopupMenu = (sectionKey) => {
  if (!sidebarOpen) {
    setPopupSection(sectionKey);
  }
};

const closePopupMenu = () => {
  if (!sidebarOpen) {
    setPopupSection(null);
  }
};

const toggleSidebar = () => {
  setSidebarOpen((prev) => !prev);
  setPopupSection(null);
};

const initialExpandedSections = {
  applications: false,
  operations: false,
  licenses: false,
  reports: false,
  administration: false,
  configuration: false,
};

const [expandedSections, setExpandedSections] = useState(initialExpandedSections);

const toggleSection = (section) => {
  setExpandedSections((prev) => ({
    ...initialExpandedSections,
    [section]: !prev[section],
  }));
};

  const isRouteActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isQueryMatch = (expectedQuery = {}) => {
    const params = new URLSearchParams(location.search);
    return Object.entries(expectedQuery).every(([key, value]) => params.get(key) === value);
  };

  const isMenuItemActive = (item) => {
    if (!isRouteActive(item.path, item.exact || false)) return false;

    if (item.query) {
      return isQueryMatch(item.query);
    }

    return true;
  };

  const menuSections = [
    {
      key: 'applications',
      title: 'Applications',
      icon: '📄',
      items: [
        { label: 'All Applications', path: '/office/applications', exact: true },
        { label: 'New Applications', path: '/office/applications', query: { status: 'submitted' } },
        { label: 'In Progress Applications', path: '/office/applications', query: { status: 'in_progress' } },
        { label: 'Approved Applications', path: '/office/applications', query: { status: 'approved' } },
        { label: 'Paid Applications', path: '/office/applications', query: { status: 'paid' } },
        { label: 'License Issued', path: '/office/applications', query: { status: 'issued' } },
        { label: 'Rejected Applications', path: '/office/applications', query: { status: 'rejected' } },
      ],
    },
    {
      key: 'counter',
      title: 'Counter Operation',
      icon: '🏢',
      items: [
        { label: 'Create New Application', path: '/office/applications/new', exact: true },
        { label: 'Collect Payments', path: '/office/counter/payments' },
      ],
    },
    {
      key: 'operations',
      title: 'Operations',
      icon: '⚙️',
      items: [
        { label: 'Pending Approvals', path: '/office/pending-approvals' },
        { label: 'Payments', path: '/office/payments' },
      ],
    },
    {
      key: 'licenses',
      title: 'Licenses',
      icon: '🪪',
      items: [
        { label: 'All Licenses', path: '/office/licenses' },
      ],
    },
    {
      key: 'reports',
      title: 'Reports',
      icon: '📊',
      items: [
        { label: 'Reports', path: '/office/reports' },
      ],
    },
    {
      key: 'administration',
      title: 'Administration',
      icon: '👥',
      items: [
        { label: 'Users', path: '/office/users' },
        { label: 'Audit Logs', path: '/office/audit-logs' },
      ],
    },
    {
      key: 'configuration',
      title: 'Configuration',
      icon: '🛠️',
      items: [
        { label: 'Settings', path: '/office/settings' },
      ],
    },
  ];

  const getCurrentPageTitle = () => {
    const pathname = location.pathname;

    if (pathname.includes('/dashboard')) return 'Dashboard';
    if (pathname.includes('/applications/new')) return 'New Application';
    if (pathname.includes('/counter/payments')) return 'Counter Payment Collection';
    if (/\/applications\/\d+/.test(pathname)) return 'Application Details';
    if (pathname.includes('/applications')) return 'Applications';
    if (pathname.includes('/pending-approvals')) return 'Pending Approvals';
    if (pathname.includes('/inspections')) return 'Inspections';
    if (pathname.includes('/payments')) return 'Payments';
    if (pathname.includes('/licenses')) return 'Licenses';
    if (pathname.includes('/reports')) return 'Reports';
    if (pathname.includes('/users')) return 'User Management';
    if (pathname.includes('/audit-logs')) return 'Audit Logs';
    if (pathname.includes('/settings')) return 'Settings';

    return 'Office Portal';
  };

  const handleMenuNavigation = (item) => {
    const queryString = item.query
      ? `?${new URLSearchParams(item.query).toString()}`
      : '';

    navigate(`${item.path}${queryString}`);
  };

  if (authLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <OfficeLogin onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="office-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h2>Office Portal</h2>
          <button
            className="toggle-btn"
            onClick={toggleSidebar}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            type="button"
          >
            {sidebarOpen ? '←' : '→'}
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li className={`menu-item ${isRouteActive('/office/dashboard', true) ? 'active' : ''}`}>
              <button
                type="button"
                className={`menu-link single ${isRouteActive('/office/dashboard', true) ? 'active' : ''}`}
                onClick={() => {
                  navigate('/office/dashboard');
                  setPopupSection(null);
                }}
                title={!sidebarOpen ? 'Dashboard' : ''}
              >
                <span className="menu-icon">🏠</span>
                <span className="menu-label">Dashboard</span>
              </button>
            </li>

            {menuSections.map((section) => {
              const expanded = expandedSections[section.key];
              const hasActiveChild = section.items.some((item) => isMenuItemActive(item));
              const showPopup = !sidebarOpen && popupSection === section.key;

              return (
                <li
                  key={section.key}
                  className={`nav-section menu-item ${sidebarOpen && expanded ? 'expanded' : ''} ${hasActiveChild ? 'active' : ''}`}
                  onMouseEnter={() => openPopupMenu(section.key)}
                  onMouseLeave={closePopupMenu}
                >
                  <button
                    type="button"
                    className={`menu-toggle ${hasActiveChild ? 'active' : ''}`}
                    onClick={() => {
                      if (!sidebarOpen) {
                        setPopupSection((prev) => (prev === section.key ? null : section.key));
                        return;
                      }
                      toggleSection(section.key);
                    }}
                    title={!sidebarOpen ? section.title : ''}
                  >
                    <span className="menu-icon">{section.icon}</span>
                    <span className="menu-label">{section.title}</span>
                    <span className="menu-arrow">›</span>
                  </button>

                  <ul
                    className={
                      sidebarOpen
                        ? `submenu ${expanded ? 'expanded' : 'collapsed'}`
                        : `submenu ${showPopup ? 'popup-expanded' : 'popup-collapsed'}`
                    }
                  >
                    {!sidebarOpen && (
                      <li className="submenu-popup-title">
                        <span>{section.title}</span>
                      </li>
                    )}

                    {section.items.map((item) => {
                      const active = isMenuItemActive(item);

                      return (
                        <li key={`${item.path}-${item.label}`} className={active ? 'active' : ''}>
                          <button
                            type="button"
                            className={`submenu-link ${active ? 'active' : ''}`}
                            onClick={() => {
                              handleMenuNavigation(item);
                              setPopupSection(null);
                            }}
                            title={!sidebarOpen ? item.label : ''}
                          >
                            <span className="menu-label">{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      <div className={`office-main ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        <header className="office-header">
          <div className="header-left">
            {/* <button className="sidebar-toggle" onClick={toggleSidebar} type="button">
              ☰
            </button> */}
            <h1>{getCurrentPageTitle()}</h1>
          </div>

          <div className="header-right">
            <div className="notifications">
              <button className="notification-btn" type="button">
                🔔
                {notifications.length > 0 && (
                  <span className="notification-badge">{notifications.length}</span>
                )}
              </button>
            </div>

            <div className="user-menu">
              <button
                className="user-btn"
                onClick={() => setDropdownOpen((prev) => !prev)}
                type="button"
              >
                <img
                  src="https://ui-avatars.com/api/?name=Office+User&background=0066cc&color=fff"
                  alt="User avatar"
                />
                <span>{user?.name || 'Office User'}</span>
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  <button type="button" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="breadcrumbs">
          Home / {getCurrentPageTitle()}
        </div>

        <main className="office-content">
          <Routes>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="applications" element={<ApplicationsList />} />
            <Route path="pending-approvals" element={<PendingApprovalsList />} />
            <Route path="counter/payments" element={<CounterPaymentCollection />} />
            <Route path="applications/new" element={<ApplicationCreate />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="inspections" element={<InspectionsList />} />
            <Route path="payments" element={<PaymentsList />} />
            <Route path="licenses" element={<LicensesList />} />
            <Route path="licenses/certificate/:license_number" element={<CertificateView adminMode />} />
            <Route path="reports" element={<ReportsList />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="settings" element={<Settings />} />
            <Route path="certificate/:license_number" element={<CertificateView adminMode />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default OfficeLayout;